'use client'

import React from "react";
import classNames from "classnames";
import { TableSideProps } from "../../TableMap/types";
import { useTableMapState } from "../../TableMap/TableMapState";
import { ChevronDownIcon } from "@/migrated-widgets/table-map/components/icons/ChevronDownIcon";
import { CloseIcon } from "@/migrated-widgets/table-map/components/icons/CloseIcon";
import Dropdown from "../../../molecules/Dropdown";
import { Button } from "@/components/atoms";
import { DropdownItem } from "../../../molecules/Dropdown/Item";
import { CheckIcon } from "lucide-react";
import { InventoryRow, InventoryCell } from "./components";
import ZipCodesView from "./ZipCodesView";
import ByModelsView from "./ByModelsView";

const InventoryTable: React.FC<TableSideProps> = () => {
  const {
    setZipCodes, zipCodes: activeZipCodes,
    triggerZoomReset, showingTransactionType, setShowingTransactionType,
    tableView, setTableView,
    setMakes, setModels, setVehicleTypes,
  } = useTableMapState();

  const resetAllFilters = () => {
    setZipCodes([]);
    triggerZoomReset();
  };

  const changeView = (view: typeof tableView) => {
    setTableView(view);
    setZipCodes([]);
    setMakes([]);
    setModels([]);
    setVehicleTypes([]);
  };

  return (
    <div className="flex flex-col h-full min-h-0 rounded-bl-2xl overflow-visible">
      {activeZipCodes.length > 0 && tableView === 'byModels' &&
        <InventoryRow className="bg-table-map-gray font-bold">
          <InventoryCell className="text-white">Zip Code: {activeZipCodes[0]}</InventoryCell>
          <InventoryCell><CloseIcon className="cursor-pointer" onClick={resetAllFilters} /></InventoryCell>
        </InventoryRow>
      }

      {/* Transaction type selector */}
      <InventoryRow className="sticky z-101 top-0 bg-table-map-lightGray font-bold px-4!">
        <InventoryCell className={classNames('w-full text-center', 'flex justify-center items-center')}>
          <Dropdown
            closeOnClick
            dropdownList={{ className: 'w-full left-0' }}
            itemsContainerClassName="items-stretch w-full"
            trigger={
              <Button variant="ghost" className={classNames('w-full', 'text-base', 'py-0 mb-0!', 'px-6!', 'text-left! justify-between!', 'flex', 'items-center')}>
                <div>
                  {showingTransactionType === "S" ? "Sales Transactions" : ""}
                  {showingTransactionType === "R" ? "Repair Order Transactions" : ""}
                </div>
                <ChevronDownIcon width={16} height={16} className="ml-2" />
              </Button>
            }
          >
            <DropdownItem onClick={() => setShowingTransactionType("S")} className={classNames('w-full', 'px-4', 'py-2')}>
              <div className={classNames('w-full', 'flex', 'justify-between', 'items-center')}>
                <p className="text-nowrap">Sales Transactions</p>
                <span className="pl-6">{showingTransactionType === "S" ? (<CheckIcon width={16} height={16} className="ml-2" />) : null}</span>
              </div>
            </DropdownItem>
            <DropdownItem onClick={() => setShowingTransactionType("R")} className={classNames('w-full', 'px-4', 'py-2')}>
              <div className={classNames('w-full', 'flex', 'justify-between', 'items-center')}>
                <p className="text-nowrap">Repair Order Transactions</p>
                <span className="pl-6">{showingTransactionType === "R" ? (<CheckIcon width={16} height={16} className="ml-2" />) : null}</span>
              </div>
            </DropdownItem>
          </Dropdown>
        </InventoryCell>
      </InventoryRow>

      {/* View selector */}
      <InventoryRow className="sticky z-100 top-0 bg-[#f0f4f4] border-b border-b-table-map-lightGray">
        <InventoryCell className="w-full flex items-center font-bold">
          <Dropdown
            closeOnClick
            dropdownList={{ className: 'w-[280px] left-[-24px]' }}
            itemsContainerClassName="items-stretch w-full"
            trigger={
              <Button variant="ghost" className={classNames('w-full', 'text-base', 'py-0 mb-0!', 'px-0!', 'text-left! justify-between!', 'flex', 'items-center')}>
                <div className="flex items-center gap-2">
                  <span className="text-table-map-gray font-medium">View:</span>
                  <span className="font-medium text-sm">
                    {tableView === 'byModels' ? 'By Models' : tableView === 'zipCodesNew' ? 'Zip Codes - New' : 'Zip Codes - Used'}
                  </span>
                </div>
                <ChevronDownIcon width={16} height={16} className="ml-2" />
              </Button>
            }
          >
            <DropdownItem onClick={() => changeView('byModels')} className={classNames('w-full', 'px-4', 'py-2')}>
              <div className={classNames('w-full', 'flex', 'justify-between', 'items-center')}>
                <p className="text-nowrap">By Models</p>
                <span className="pl-6">{tableView === 'byModels' ? (<CheckIcon width={16} height={16} className="ml-2" />) : null}</span>
              </div>
            </DropdownItem>
            <DropdownItem onClick={() => changeView('zipCodesNew')} className={classNames('w-full', 'px-4', 'py-2')}>
              <div className={classNames('w-full', 'flex', 'justify-between', 'items-center')}>
                <p className="text-nowrap">Zip Codes - New</p>
                <span className="pl-6">{tableView === 'zipCodesNew' ? (<CheckIcon width={16} height={16} className="ml-2" />) : null}</span>
              </div>
            </DropdownItem>
            <DropdownItem onClick={() => changeView('zipCodesUsed')} className={classNames('w-full', 'px-4', 'py-2')}>
              <div className={classNames('w-full', 'flex', 'justify-between', 'items-center')}>
                <p className="text-nowrap">Zip Codes - Used</p>
                <span className="pl-6">{tableView === 'zipCodesUsed' ? (<CheckIcon width={16} height={16} className="ml-2" />) : null}</span>
              </div>
            </DropdownItem>
          </Dropdown>
        </InventoryCell>
      </InventoryRow>

      {/* Scrollable table content */}
      <div className="flex flex-col items-center overflow-y-auto visible-scrollbar flex-1 min-h-0">
        {tableView === 'zipCodesNew' && <ZipCodesView vehicleType="NEW" />}
        {tableView === 'zipCodesUsed' && <ZipCodesView vehicleType="USED" />}
        {tableView === 'byModels' && <ByModelsView />}
      </div>
    </div>
  );
};

export default InventoryTable;
